// Utility functions for whiteboard text and image-text formatting

export const getSelectedTextElement = () => {
  const selection = window.getSelection();
  if (selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);
  const container = range.commonAncestorContainer;

  let textElement = container.nodeType === Node.TEXT_NODE ? container.parentElement : container;
  while (textElement && !textElement.hasAttribute('data-shape-id')) {
    textElement = textElement.parentElement;
  }

  return textElement;
};

export const getSelectedTextFontSize = () => {
  const selection = window.getSelection();
  if (selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);

  if (!selection.isCollapsed) {
    const span = range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentElement;

    if (span && span.style.fontSize) {
      return span.style.fontSize;
    }

    const walker = document.createTreeWalker(
      range.commonAncestorContainer,
      NodeFilter.SHOW_ELEMENT,
      null,
      false
    );

    let node;
    // eslint-disable-next-line no-cond-assign
    while (node = walker.nextNode()) {
      if (node.style.fontSize) {
        return node.style.fontSize;
      }
    }
  }

  return null;
};

const applyFormattingToTextElement = (textElement, property, value) => {
  if (!textElement) return;

  const selection = window.getSelection();
  if (selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0);

  if (range.toString() === textElement.textContent) {
    textElement.style[property] = value;
    return;
  }

  const span = document.createElement('span');
  span.style[property] = value;

  try {
    range.surroundContents(span);
  } catch (e) {
    textElement.style[property] = value;
  }
};

// eslint-disable-next-line no-unused-vars
export const applyTextStyle = (property, value) => {
  const textElement = getSelectedTextElement();
  if (!textElement) return;
  applyFormattingToTextElement(textElement, property, value);
};

export const applyBold = () => {
  if (window.selectedWhiteboardTextShape) {
    const { shape, updateFn } = window.selectedWhiteboardTextShape;
    updateFn({ bold: !shape.bold });
    return;
  }
  const textElement = getSelectedTextElement();
  if (!textElement) return;

  const selection = window.getSelection();
  if (selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0);
  const isSticker = textElement.style.boxShadow && textElement.style.boxShadow.includes('rgba(0,0,0,0.12)');

  if (isSticker) {
    const isBold = textElement.style.fontWeight === 'bold';
    textElement.style.fontWeight = isBold ? 'normal' : 'bold';
  } else {
    const isBold = textElement.style.fontWeight === 'bold';
    if (range.toString() === textElement.textContent) {
      textElement.style.fontWeight = isBold ? 'normal' : 'bold';
    } else {
      const span = document.createElement('span');
      span.style.fontWeight = isBold ? 'normal' : 'bold';
      try { range.surroundContents(span); } catch (e) { textElement.style.fontWeight = isBold ? 'normal' : 'bold'; }
    }
  }
};

export const applyItalic = () => {
  if (window.selectedWhiteboardTextShape) {
    const { shape, updateFn } = window.selectedWhiteboardTextShape;
    updateFn({ italic: !shape.italic });
    return;
  }
  const textElement = getSelectedTextElement();
  if (!textElement) return;

  const selection = window.getSelection();
  if (selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0);
  const isSticker = textElement.style.boxShadow && textElement.style.boxShadow.includes('rgba(0,0,0,0.12)');

  if (isSticker) {
    const isItalic = textElement.style.fontStyle === 'italic';
    textElement.style.fontStyle = isItalic ? 'normal' : 'italic';
  } else {
    const isItalic = textElement.style.fontStyle === 'italic';
    if (range.toString() === textElement.textContent) {
      textElement.style.fontStyle = isItalic ? 'normal' : 'italic';
    } else {
      const span = document.createElement('span');
      span.style.fontStyle = isItalic ? 'normal' : 'italic';
      try { range.surroundContents(span); } catch (e) { textElement.style.fontStyle = isItalic ? 'normal' : 'italic'; }
    }
  }
};

export const applyUnderline = () => {
  if (window.selectedWhiteboardTextShape) {
    const { shape, updateFn } = window.selectedWhiteboardTextShape;
    updateFn({ underline: !shape.underline });
    return;
  }
  const textElement = getSelectedTextElement();
  if (!textElement) return;

  const selection = window.getSelection();
  if (selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0);
  const isSticker = textElement.style.boxShadow && textElement.style.boxShadow.includes('rgba(0,0,0,0.12)');

  if (isSticker) {
    const isUnderlined = textElement.style.textDecoration.includes('underline');
    textElement.style.textDecoration = isUnderlined ? 'none' : 'underline';
  } else {
    const isUnderlined = textElement.style.textDecoration.includes('underline');
    if (range.toString() === textElement.textContent) {
      textElement.style.textDecoration = isUnderlined ? 'none' : 'underline';
    } else {
      const span = document.createElement('span');
      span.style.textDecoration = isUnderlined ? 'none' : 'underline';
      try { range.surroundContents(span); } catch (e) { textElement.style.textDecoration = isUnderlined ? 'none' : 'underline'; }
    }
  }
};

export const applyStrikethrough = () => {
  if (window.selectedWhiteboardTextShape) {
    const { shape, updateFn } = window.selectedWhiteboardTextShape;
    updateFn({ strikethrough: !shape.strikethrough });
    return;
  }
  const textElement = getSelectedTextElement();
  if (!textElement) return;

  const selection = window.getSelection();
  if (selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0);
  const isSticker = textElement.style.boxShadow && textElement.style.boxShadow.includes('rgba(0,0,0,0.12)');

  if (isSticker) {
    const isStrikethrough = textElement.style.textDecoration.includes('line-through');
    textElement.style.textDecoration = isStrikethrough ? 'none' : 'line-through';
  } else {
    const isStrikethrough = textElement.style.textDecoration.includes('line-through');
    if (range.toString() === textElement.textContent) {
      textElement.style.textDecoration = isStrikethrough ? 'none' : 'line-through';
    } else {
      const span = document.createElement('span');
      span.style.textDecoration = isStrikethrough ? 'none' : 'line-through';
      try { range.surroundContents(span); } catch (e) { textElement.style.textDecoration = isStrikethrough ? 'none' : 'line-through'; }
    }
  }
};

export const applyTextAlign = (alignment) => {
  if (window.selectedWhiteboardTextShape) {
    window.selectedWhiteboardTextShape.updateFn({ textAlign: alignment });
    return;
  }
  const textElement = getSelectedTextElement();
  if (!textElement) return;
  textElement.style.textAlign = alignment;
};

const focusTextInputIfActive = () => {
  setTimeout(() => {
    const textInput = document.querySelector('.image-text-input');
    if (textInput && window.selectedImageTextElement) textInput.focus();
  }, 0);
};

const triggerToolbarUpdate = () => {
  if (window.updateToolbar) window.updateToolbar();
};

export const applyImageTextFontFamily = (fontFamily, textElementData) => {
  if (!textElementData || !textElementData.updateCallback) return;
  textElementData.updateCallback({ fontFamily });
  if (window.selectedImageTextElement?.id === textElementData.id) {
    window.selectedImageTextElement.fontFamily = fontFamily;
  }
  triggerToolbarUpdate();
  focusTextInputIfActive();
};

export const applyImageTextFontSize = (fontSize, textElementData) => {
  if (!textElementData || !textElementData.updateCallback) return;
  const fontSizeNum = parseInt(fontSize);
  textElementData.updateCallback({ fontSize: fontSizeNum });
  if (window.selectedImageTextElement?.id === textElementData.id) {
    window.selectedImageTextElement.fontSize = fontSizeNum;
  }
  triggerToolbarUpdate();
  focusTextInputIfActive();
};

export const applyImageTextColor = (color, textElementData) => {
  if (!textElementData || !textElementData.updateCallback) return;
  textElementData.updateCallback({ color });
  if (window.selectedImageTextElement?.id === textElementData.id) {
    window.selectedImageTextElement.color = color;
  }
  triggerToolbarUpdate();
  focusTextInputIfActive();
};

export const applyImageTextBold = (textElementData) => {
  if (!textElementData || !textElementData.updateCallback) return;
  const currentBold = textElementData.fontWeight === 'bold';
  const newWeight = currentBold ? 'normal' : 'bold';
  textElementData.updateCallback({ fontWeight: newWeight });
  if (window.selectedImageTextElement?.id === textElementData.id) {
    window.selectedImageTextElement.fontWeight = newWeight;
  }
  triggerToolbarUpdate();
  focusTextInputIfActive();
};

export const applyImageTextItalic = (textElementData) => {
  if (!textElementData || !textElementData.updateCallback) return;
  const currentItalic = textElementData.fontStyle === 'italic';
  const newStyle = currentItalic ? 'normal' : 'italic';
  textElementData.updateCallback({ fontStyle: newStyle });
  if (window.selectedImageTextElement?.id === textElementData.id) {
    window.selectedImageTextElement.fontStyle = newStyle;
  }
  triggerToolbarUpdate();
  focusTextInputIfActive();
};

export const applyImageTextUnderline = (textElementData) => {
  if (!textElementData || !textElementData.updateCallback) return;
  const currentDecoration = textElementData.textDecoration || '';
  const hasUnderline = currentDecoration.includes('underline');
  const newDecoration = hasUnderline
    ? currentDecoration.replace('underline', '').trim() || 'none'
    : (currentDecoration && currentDecoration !== 'none' ? currentDecoration + ' underline' : 'underline');
  textElementData.updateCallback({ textDecoration: newDecoration });
  if (window.selectedImageTextElement?.id === textElementData.id) {
    window.selectedImageTextElement.textDecoration = newDecoration;
  }
  triggerToolbarUpdate();
  focusTextInputIfActive();
};

export const applyImageTextStrikethrough = (textElementData) => {
  if (!textElementData || !textElementData.updateCallback) return;
  const currentDecoration = textElementData.textDecoration || '';
  const hasStrikethrough = currentDecoration.includes('line-through');
  const newDecoration = hasStrikethrough
    ? currentDecoration.replace('line-through', '').trim() || 'none'
    : (currentDecoration && currentDecoration !== 'none' ? currentDecoration + ' line-through' : 'line-through');
  textElementData.updateCallback({ textDecoration: newDecoration });
  if (window.selectedImageTextElement?.id === textElementData.id) {
    window.selectedImageTextElement.textDecoration = newDecoration;
  }
  triggerToolbarUpdate();
  focusTextInputIfActive();
};

export const applyFontSize = (size) => {
  if (window.selectedWhiteboardTextShape) {
    window.selectedWhiteboardTextShape.updateFn({ fontSize: parseInt(size, 10) });
    return;
  }
  if (window.selectedImageTextElement) {
    applyImageTextFontSize(size, window.selectedImageTextElement);
    return;
  }

  const textElement = getSelectedTextElement();
  if (!textElement) return;

  const selection = window.getSelection();
  if (selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0);
  const isSticker = textElement.style.boxShadow && textElement.style.boxShadow.includes('rgba(0,0,0,0.12)');

  if (isSticker) {
    textElement.style.fontSize = size + 'px';
  } else {
    if (selection.isCollapsed || range.toString() === textElement.textContent) {
      textElement.style.fontSize = size + 'px';
    } else {
      const span = document.createElement('span');
      span.style.fontSize = size + 'px';
      try {
        range.surroundContents(span);
        setTimeout(() => {
          const newRange = document.createRange();
          newRange.setStart(span.firstChild, 0);
          newRange.setEnd(span.lastChild, span.lastChild.textContent.length);
          selection.removeAllRanges();
          selection.addRange(newRange);
        }, 0);
      } catch (e) {
        textElement.style.fontSize = size + 'px';
      }
    }
  }
};

export const applyFontFamily = (fontFamily) => {
  if (window.selectedWhiteboardTextShape) {
    window.selectedWhiteboardTextShape.updateFn({ font: fontFamily });
    return;
  }
  if (window.selectedImageTextElement) {
    applyImageTextFontFamily(fontFamily, window.selectedImageTextElement);
    return;
  }

  const textElement = getSelectedTextElement();
  if (!textElement) return;

  const selection = window.getSelection();
  if (selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0);
  const isSticker = textElement.style.boxShadow && textElement.style.boxShadow.includes('rgba(0,0,0,0.12)');

  if (isSticker) {
    textElement.style.fontFamily = fontFamily;
  } else {
    if (selection.isCollapsed || range.toString() === textElement.textContent) {
      textElement.style.fontFamily = fontFamily;
    } else {
      const span = document.createElement('span');
      span.style.fontFamily = fontFamily;
      try {
        range.surroundContents(span);
        setTimeout(() => {
          const newRange = document.createRange();
          newRange.setStart(span.firstChild, 0);
          newRange.setEnd(span.lastChild, span.lastChild.textContent.length);
          selection.removeAllRanges();
          selection.addRange(newRange);
        }, 0);
      } catch (e) {
        textElement.style.fontFamily = fontFamily;
      }
    }
  }
};

export const applyColor = (color) => {
  if (window.selectedWhiteboardTextShape) {
    window.selectedWhiteboardTextShape.updateFn({ color });
    return;
  }
  if (window.selectedImageTextElement) {
    applyImageTextColor(color, window.selectedImageTextElement);
    return;
  }

  const textElement = getSelectedTextElement();
  if (!textElement) return;

  const selection = window.getSelection();
  if (selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0);
  const isSticker = textElement.style.boxShadow && textElement.style.boxShadow.includes('rgba(0,0,0,0.12)');

  if (isSticker) {
    textElement.style.color = color;
  } else {
    if (range.toString() === textElement.textContent) {
      textElement.style.color = color;
    } else {
      const span = document.createElement('span');
      span.style.color = color;
      try { range.surroundContents(span); } catch (e) { textElement.style.color = color; }
    }
  }
};

export const applyHighlight = (color) => {
  if (window.selectedWhiteboardTextShape) {
    window.selectedWhiteboardTextShape.updateFn({ highlight: color });
    return;
  }
  const textElement = getSelectedTextElement();
  if (!textElement) return;

  const selection = window.getSelection();
  if (selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0);
  const isSticker = textElement.style.boxShadow && textElement.style.boxShadow.includes('rgba(0,0,0,0.12)');

  if (isSticker) {
    textElement.style.backgroundColor = color;
  } else {
    if (range.toString() === textElement.textContent) {
      textElement.style.backgroundColor = color;
    } else {
      const span = document.createElement('span');
      span.style.backgroundColor = color;
      try { range.surroundContents(span); } catch (e) { textElement.style.backgroundColor = color; }
    }
  }
};

export const applyBulletList = () => {
  const textElement = getSelectedTextElement();
  if (!textElement) return;

  const hasBullet = textElement.textContent.startsWith('• ');
  const hasNumber = /^\d+\.\s/.test(textElement.textContent);

  if (hasBullet || hasNumber) {
    if (hasBullet) textElement.textContent = textElement.textContent.replace(/^•\s/, '');
    else if (hasNumber) textElement.textContent = textElement.textContent.replace(/^\d+\.\s/, '');
    textElement.style.paddingLeft = '0';
  } else {
    textElement.textContent = '• ' + textElement.textContent;
    textElement.style.color = '#000000';
    textElement.style.paddingLeft = '20px';
  }
};

export const applyOrderedList = () => {
  const textElement = getSelectedTextElement();
  if (!textElement) return;

  const hasBullet = textElement.textContent.startsWith('• ');
  const hasNumber = /^\d+\.\s/.test(textElement.textContent);

  if (hasBullet || hasNumber) {
    if (hasBullet) textElement.textContent = textElement.textContent.replace(/^•\s/, '');
    else if (hasNumber) textElement.textContent = textElement.textContent.replace(/^\d+\.\s/, '');
    textElement.style.paddingLeft = '0';
  } else {
    textElement.textContent = '1. ' + textElement.textContent;
    textElement.style.color = '#000000';
    textElement.style.paddingLeft = '20px';
  }
};
